import { IStage } from 'data/stage';
import { Stage as DistributionStage } from '@org-avp/models';
import { safeUrl } from 'utils/url';
import { IBrand } from 'data/schema';

export const EXHIBITION_STAGE: IStage = (process.env.STAGE as IStage) || 'dev';

export type StreamingStage = 'qa' | 'prelive' | 'prelive-euc1' | 'prod' | 'prod-euw1' | 'prod-euc1';
export type MiwiStage = 'dev' | 'prelive' | 'live';

/**
 * Enum representing the base URIs for different streaming environments.
 * Each environment has a specific base URI for accessing streaming services.
 */
export enum ExhibitionStandaloneUriMapping {
  test = 'https://test-3dexhibition.avp.tech',
  dev = 'https://dev-3dexhibition.avp.tech',
  prelive = 'https://prelive-3dexhibition.avp.tech',
  prod = 'https://3dexhibition.avp.tech',
}

const streamingStageMapping: Record<IStage, StreamingStage> = {
  test: 'qa',
  dev: 'qa',
  prelive: 'prelive',
  // "prod" is not an actual streaming stage, but when using this, 3D Showroom will figure out the best fitting region automatically
  // this e.g. leads to users in the US being more likely to get served a US instance for lower latency
  // note that this logic of figuring out the best region is only available in production
  prod: 'prod',
};

const miWiStageMapping: Record<IStage, MiwiStage> = {
  test: 'dev',
  dev: 'dev',
  prelive: 'prelive',
  prod: 'live',
};

const distributionStageMapping: Record<IStage, DistributionStage> = {
  test: DistributionStage.DEVELOP,
  dev: DistributionStage.DEVELOP,
  prelive: DistributionStage.PREPROD,
  prod: DistributionStage.PROD,
};

export function getStreamingStage(brand?: IBrand) {
  if ((brand === 'L' || brand === 'A') && EXHIBITION_STAGE === 'prelive') {
    // Special case for A & L brand in prelive stage
    return 'prelive-euc1';
  }

  return streamingStageMapping[EXHIBITION_STAGE];
}

export function getMiwiStage() {
  return miWiStageMapping[EXHIBITION_STAGE];
}

// Used for Team Distribution devices API
export function getDevicesStage(): DistributionStage {
  return distributionStageMapping[EXHIBITION_STAGE];
}

export function getStageOrigin() {
  const hostname = globalThis?.window?.location?.hostname;

  // Use window.location.origin for localhost
  if (hostname === 'localhost' && process.env.API_URL?.includes('localhost')) {
    return window.location.origin;
  }

  // Use window.location.origin for PR domains (e.g., pr740.test-3dexhibition.avp.tech)
  if (hostname && /^pr\d+\./.test(hostname)) {
    return window.location.origin;
  }

  const uri =
    ExhibitionStandaloneUriMapping[EXHIBITION_STAGE.toLowerCase() as keyof typeof ExhibitionStandaloneUriMapping];
  return uri || ExhibitionStandaloneUriMapping.dev;
}

/**
 * Robustly combines the base URL and asset path, handling slashes, subfolders, relative paths, and using safeUrl.
 * Satisfies all requirements from the test suite.
 */
export function webComponentAssetUrl(assetPath: string): string {
  // The web-components build already emits absolute asset URLs on the build's own release
  if (/^https?:\/\//i.test(assetPath)) {
    return assetPath;
  }

  // Determine the base URL
  const baseUrl = process.env.WC_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

  // Remove duplicate slashes from baseUrl
  const baseUrlClean = safeUrl(baseUrl);

  // If assetPath is relative (e.g. '../assets/image.png'), use URL constructor for resolution
  if (assetPath.startsWith('..')) {
    try {
      const url = new URL(assetPath, baseUrlClean + '/');
      return url.href;
    } catch {
      // fallback to string join below
    }
  }

  // Remove leading slashes from assetPath (but keep one if present)
  const assetPathClean = assetPath.replace(/^\/+/, '');

  // If baseUrl ends with a subfolder, and assetPath starts with '/', join them
  // If assetPath starts with '/', treat it as relative to baseUrl's path
  let combinedUrl: string;
  try {
    // Use URL constructor for robust joining
    const url = new URL(baseUrlClean.endsWith('/') ? baseUrlClean : baseUrlClean + '/');
    if (assetPath.startsWith('/')) {
      // If baseUrl has a path, append assetPath to it
      url.pathname = url.pathname.replace(/\/+$/, '') + assetPath;
    } else {
      url.pathname = url.pathname.replace(/\/+$/, '') + '/' + assetPathClean;
    }
    combinedUrl = url.href;
  } catch {
    // fallback to string join
    combinedUrl = baseUrlClean + '/' + assetPathClean;
  }

  // Remove any double slashes except after protocol
  combinedUrl = combinedUrl.replace(/([^:])\/+/g, '$1/');

  return combinedUrl;
}
